<template>
  <div class="h-[500px]">
    <ag-grid-vue
      theme="legacy"
      :rowData="simpleWaivers"
      :columnDefs="colDefs"
      :defaultColDef="defaultColDefs"
      :autoSizeStrategy="{ type: 'fitGridWidth' }"
      :stopEditingWhenCellsLoseFocus="true"
      :suppressDragLeaveHidesColumns="true"
      class="py-3 h-full"
      :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
      :singleClickEdit="true"
      :pinnedBottomRowData="[{}]"
      :isFullWidthRow="(params: IsFullWidthRowParams) => params.rowNode.rowPinned === 'bottom'"
      :fullWidthCellRenderer="CustomAddNewCellRenderer"
      :fullWidthCellRendererParams="{
        callback: () => addNewSimpleWaiver(),
        label: 'waiver',
        display: canEdit,
      }"
      @grid-size-changed="resize"
      @grid-ready="(event: GridReadyEvent) => gridApi = event.api"
    ></ag-grid-vue>
  </div>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import CustomTriStateCheckboxRenderer from "@/components/cell-renderers/CustomTriStateCheckboxRenderer.vue";
import CustomDeleteIconCellRenderer from "@/components/cell-renderers/CustomDeleteIconCellRenderer.vue";
import CustomAddNewCellRenderer from "@/components/cell-renderers/CustomAddNewCellRenderer.vue";
import { usePropsStore } from "@/stores/props";
import type { SimpleWaiver } from "@/types/Waiver";
import { currencyFormatter, getTableData, numberParser } from "@/services/Utility";
import type { GridApi, GridReadyEvent, IsFullWidthRowParams, ValueParserParams } from "ag-grid-community";

const simpleWaivers = defineModel<SimpleWaiver[]>("simpleWaivers", { required: true });

interface Props {
  projectId: string;
  canEdit: boolean;
}
const props = defineProps<Props>();

const gridApi = ref<GridApi>();
const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const resize = (): void => {
  gridApi.value?.autoSizeColumns([]);
};

const defaultColDefs = {
  floatingFilter: false,
  enableCellChangeFlash: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  headerClass: "grid-align-center text-center",
  cellDataType: "text",
};

const addNewSimpleWaiver = (): void => {
  const newSimpleWaiver: SimpleWaiver = {
    waiverId: null,
    projectId: props.projectId,
    description: "",
    amount: 0,
    conLienWaiver: null,
    uncLienWaiver: null,
    notes: "",
  };

  gridApi.value?.applyTransaction({
    add: [newSimpleWaiver],
  });
  simpleWaivers.value = getTableData(gridApi.value);
};

const deleteSimpleWaiver = (simpleWaiver: SimpleWaiver): void => {
  gridApi.value?.applyTransaction({
    remove: [simpleWaiver],
  });

  simpleWaivers.value = getTableData(gridApi.value);
  success("Simple Waiver Deleted", "A waiver was successfully deleted");
};

const conLienWaiverChanged = (rowIndex: number, conLienWaiver: boolean | null): void => {
  const rowData = gridApi.value?.getDisplayedRowAtIndex(rowIndex);
  if (rowData) rowData.setDataValue("conLienWaiver", conLienWaiver);
};

const uncLienWaiverChanged = (rowIndex: number, uncLienWaiver: boolean | null): void => {
  const rowData = gridApi.value?.getDisplayedRowAtIndex(rowIndex);
  if (rowData) rowData.setDataValue("uncLienWaiver", uncLienWaiver);
};

const colDefs = ref([
  {
    field: "delete",
    headerName: "",
    cellClass: "text-center flex items-center justify-center",
    cellRenderer: CustomDeleteIconCellRenderer,
    cellRendererParams: {
      deleteConfirmMessage: (waiver: SimpleWaiver) => `Are you sure you want to delete this waiver "${waiver.description}"?`,
      deleteConfirmHeader: () => "Delete Waiver",
      callback: deleteSimpleWaiver,
      checkDeletable: (waiver: SimpleWaiver) => props.canEdit,
    },
    width: 50,
    minWidth: 50,
    hide: !props.canEdit,
  },
  {
    field: "description",
    headerName: "Description",
    width: 200,
    editable: props.canEdit,
    valueParser: (params: ValueParserParams) => (params.newValue == null ? "" : params.newValue),
  },
  {
    field: "amount",
    cellDataType: "number",
    headerName: "Amount",
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => numberParser(params, 2, true),
    width: 100,
    editable: props.canEdit,
  },
  {
    field: "conLienWaiver",
    cellDataType: "boolean",
    headerName: "Con. Lien Waiver",
    cellClass: "text-center",
    cellRenderer: CustomTriStateCheckboxRenderer,
    cellRendererParams: {
      setValue: conLienWaiverChanged,
    },
    width: 100,
  },
  {
    field: "uncLienWaiver",
    cellDataType: "boolean",
    headerName: "Unc. Lien Waiver",
    cellClass: "text-center",
    cellRenderer: CustomTriStateCheckboxRenderer,
    cellRendererParams: {
      setValue: uncLienWaiverChanged,
    },
    width: 100,
  },
  {
    field: "notes",
    headerName: "Notes",
    width: 300,
    editable: props.canEdit,
    valueParser: (params: ValueParserParams) => (params.newValue == null ? "" : params.newValue),
  },
]);
</script>
